import {
  ApiResponse,
  JobEventDescription,
  JobEventApprovalStatus,
  JobEventPublicStatus,
  JobEventSchedule,
  ApiRequest,
  EditingJobEventSchedule,
  JobEventCategory
} from "@/api/types";
import { PartiallyPartial, PartiallyRequired } from "./type-helper";

import {
  occStatusActive,
  occStatusInactiveLight,
  occStatusAlert,
  occStatusWarning
} from "@/assets/styles/vars-for-script.scss";

/**
 * URLのクエリパラメータの名前
 */
export const QueryNames = {
  TableOfContentType: "ft"
};

/** 並び替えに一意なidが必要なためidを必須にする */
export type DisplayDescriptionItem = PartiallyRequired<
  JobEventDescription,
  "id"
>;

export type ValidateKey = keyof typeof formConfig;

export const getDisplayApprovalStatus = (
  approvalStatus: JobEventApprovalStatus
): string => {
  switch (approvalStatus) {
    case "approved":
      return "承認済み";
    case "waiting_for_publish":
      return "承認済み";
    case "draft":
      return "下書き";
    case "pending":
      return "承認待ち";
    case "rejected":
      return "棄却";
  }
};

export const getDisplayPublicStatus = (
  publicStatus: JobEventPublicStatus,
  approvalStatus: JobEventApprovalStatus | undefined = undefined
): string => {
  switch (publicStatus) {
    case "private":
      if (approvalStatus && approvalStatus === "waiting_for_publish") {
        return "公開予定";
      } else {
        return "非公開";
      }
    case "public":
      return "公開中";
    case "coming_soon":
      return "公開予定";
    case "stopped":
      return "公開終了";
  }
};

export const getApprovalStatusColorCode = (
  approvalStatus: JobEventApprovalStatus
): string => {
  switch (approvalStatus) {
    case "approved":
      return occStatusActive;
    case "waiting_for_publish":
      return occStatusActive;
    case "draft":
      return occStatusInactiveLight;
    case "pending":
      return occStatusWarning;
    case "rejected":
      return occStatusAlert;
  }
};

export const getPublicStatusColorCode = (
  publicStatus: JobEventPublicStatus,
  approvalStatus: JobEventApprovalStatus | undefined = undefined
): string => {
  switch (publicStatus) {
    case "private":
      if (approvalStatus && approvalStatus === "waiting_for_publish") {
        return occStatusWarning;
      } else {
        return occStatusAlert;
      }
    case "public":
      return occStatusActive;
    case "coming_soon":
      return occStatusWarning;
    case "stopped":
      return occStatusInactiveLight;
  }
};

export enum TableOfContentType {
  BasicInfoForm = "basicInfoForm",
  ScheduleForm = "scheduleForm",
  ResumeForm = "resumeForm",
  ReplyForm = "replyForm",
  PublishingForm = "publishingForm"
}

export const tableOfContentConfig = {
  [TableOfContentType["BasicInfoForm"]]: {
    label: "基本情報"
  },
  [TableOfContentType["ScheduleForm"]]: {
    label: "スケジュール"
  },
  [TableOfContentType["ResumeForm"]]: {
    label: "レジュメ"
  },
  [TableOfContentType["ReplyForm"]]: {
    label: "自動返信メール"
  },
  [TableOfContentType["PublishingForm"]]: {
    label: "公開設定"
  }
};

// TODO: 情報を埋める
export const formConfig = {
  title: {
    label: "タイトル",
    placeholder: "イベントタイトルを入力してください",
    help: "イベントのタイトルです。",
    required: true,
    formType: TableOfContentType["BasicInfoForm"]
  },
  eye_catch_image: {
    label: "アイキャッチ画像",
    placeholder: "横1360px × 縦400px",
    help:
      "イベントページの上部に表示される画像です。第一印象に影響するため、解像度が高く、社風が伝わりやすい画像を推奨します。1360 x 400 px / jpg推奨",
    required: true,
    formType: TableOfContentType["BasicInfoForm"]
  },
  attention_item: {
    label: "注意事項",
    placeholder: "注意事項を入力してください",
    help:
      "ワンキャリア受付の場合のみ、学生の混乱を避けるために必須 および 編集不可とさせていただきます。",
    required: true,
    content: `※ ワンキャリア運営事務局より ※
    ワンキャリアにてエントリー後、企業よりマイページ等の案内がご登録のアドレスへ送付されます。企業からの連絡まで数日お待ちいただく可能性がございますこと、予めご了承くださいませ。
    `,
    formType: TableOfContentType["BasicInfoForm"]
  },
  "event_description_items/label": {
    label: "項目名",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["BasicInfoForm"]
  },
  "event_description_items/content": {
    label: "内容",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["BasicInfoForm"]
  },
  schedules: {
    label: "スケジュール",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/title": {
    label: "タイトル",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/time_limit_at": {
    label: "申し込み期限",
    placeholder: "",
    help:
      "開始日時・終了日時・申込期限の記載がある場合、「申込期限」でスケジュールが終了いたします。",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/start_at": {
    label: "開催開始日時",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/end_at": {
    label: "開催終了日時",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/schedule_type": {
    label: "開催方法",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/place": {
    label: "開催場所",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/limited_place": {
    label: "開催場所 (当選者限定表示)",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/live_url": {
    label: "開催URL",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/prefecture": {
    label: "都道府県",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/admission_limit": {
    label: "最大申し込み可能人数",
    placeholder: "",
    help:
      "指定された人数を超過するとスケジュールが自動的に終了いたします。​​※定員の人数ではないので、ご注意ください",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/entry_stopped": {
    label: "募集停止",
    placeholder: "",
    help:
      "​​即時で募集受付が終了いたします。​​再開はワンキャリアでのみ操作可能であるため、ご希望の際はご依頼ください。",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "schedules/canceled": {
    label: "開催停止",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ScheduleForm"]
  },
  "resume_questions/title": {
    label: "質問タイトル",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ResumeForm"]
  },
  "resume_questions/description": {
    label: "説明文",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ResumeForm"]
  },
  "resume_questions/min_length": {
    label: "最小文字数",
    placeholder:
      "最大文字数は指定できないため、学生様に入力いただきたい最低文字数をご記入ください。",
    help: "",
    required: true,
    formType: TableOfContentType["ResumeForm"]
  },
  "mail_notifications/reminder": {
    label: "リマインド",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ReplyForm"]
  },
  "mail_notifications/thanks": {
    label: "参加御礼",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["ReplyForm"]
  },
  contract_id: {
    label: "対象契約（卒年度）",
    placeholder: "",
    help: "公開する対象を設定できます。",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  category: {
    label: "種別",
    placeholder: "",
    help: "イベントページのカテゴリを設定できます。",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  reception_type: {
    label: "受付方法",
    placeholder: "",
    help:
      "申込の受付先を設定できます。ワンキャリアでデータを蓄積する場合は「ワンキャリア受付」を、自社のATSなどで管理される場合は「外部受付」を選択してください。",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  application_url: {
    label: "申込先URL",
    placeholder: "申込先URL",
    help: "外部遷移先URLを記入してください",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  is_lottery: {
    label: "ワンキャリアでの抽選",
    placeholder: "",
    help:
      "ワンキャリアの管理画面上で、学生の会員登録情報やレジュメを基に抽選を実施できます。 ※ 自社内で行う面接などの選考活動のことではございませんので、ご注意ください。",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  iweb_code: {
    label: "i-webコード",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  iweb_type: {
    label: "i-web連携種別",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  iweb_graduate_year: {
    label: "連携卒業年度",
    placeholder: "",
    help: "",
    required: true,
    formType: TableOfContentType["PublishingForm"]
  },
  approval_status: {
    label: "承認ステータス",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  public_status: {
    label: "公開ステータス",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  publish_at: {
    label: "公開日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  updated_at: {
    label: "更新日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  approved_at: {
    label: "承認日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  preferred_publish_at: {
    label: "希望日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  preferred_updated_at: {
    label: "更新希望日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  scheduled_publish: {
    label: "公開希望日時",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  reject_reason: {
    label: "コメント",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  },
  comment: {
    label: "依頼メッセージ",
    placeholder: "",
    help: "",
    required: true,
    formType: undefined
  }
};

export const REQUIRED_DESCRIPTION_ITEMS = [
  {
    label: "概要" as const,
    defaultContent: "",
    placeholder: "イベント概要を入力してください",
    help: "イベントの概要（要点）を記載しましょう。"
  },
  {
    label: "お問い合わせ先" as const,
    defaultContent: "",
    placeholder: `例）人事部 採用担当 山田
電話番号：080-1234-5678
メールアドレス：saiyo@sample.com
問い合わせフォーム：https://forms.gle/AbCDEFGHIJK`,
    help: "",
    underLabel:
      "電話番号、メールアドレス、問い合わせフォームURLのいずれかを含む連絡先を記入してください。"
  }
];

export const OPTIONAL_DEFAULT_DESCRIPTION_ITEMS = [
  {
    label: "会場" as const,
    defaultContent: "",
    placeholder: "例）東京本社（東京駅より徒歩3分）",
    help:
      "イベントの会場を記載しましょう。オンライン開催の場合はその旨をご記載ください。"
  },
  {
    label: "定員" as const,
    defaultContent: "",
    placeholder: "例）30名程度",
    help: "イベントの定員を記載しましょう。"
  }
];

export type RequiredDescriptionItemLabel = typeof REQUIRED_DESCRIPTION_ITEMS[number]["label"];
export type OptionalDefaultDescriptionItemLabel = typeof OPTIONAL_DEFAULT_DESCRIPTION_ITEMS[number]["label"];

export const JOB_EVENT_MAIL_VARIABLE_PREVIEW: {
  key: ApiResponse.JobEventMailTemplateVariable["variables"][number]["name"];
  previewText: string;
}[] = [
  { key: "user_name", previewText: `[山田 太郎]` },
  { key: "graduate_year", previewText: `[卒業年度]` },
  { key: "university_name", previewText: `[大学名]` },
  { key: "event_title", previewText: `[イベント名]` },
  { key: "event_url", previewText: `[イベントページURL]` },
  { key: "schedule_title", previewText: `[スケジュール名]` }, // confl ない
  { key: "schedule_date", previewText: `●月●日（●）` },
  {
    key: "schedule_info",
    previewText: `■ イベント概要
  [イベントページURL]
  ・日程：●月●日（●）●●:●● 〜 ●●:●●
  ・場所：●●●●`
  },
  {
    key: "cancel_remarks",
    previewText: `なお、ご参加頂けない場合は、必ず【開催日の前日】までにキャンセル手続きを行ってください。
  キャンセル手続きはこちら`
  },
  {
    key: "resume_remarks",
    previewText: `※ 提出期限：●●/●●/●● ●●:●●まで
  ※ レジュメのご提出をもってエントリーが完了となります。レジュメの提出がない限りはセミナーにご参加いただけませんので必ずご提出ください。`
  }
  // university_name レスポンスない
];

export const PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県"
] as const;

export const initialJobEvent: ApiRequest.EditingDraftJobEvent = {
  id: undefined,
  approval_status: "draft",
  public_status: "private",
  scheduled_publish: false,
  event_description_items: [],
  reception_type: "internal",
  is_lottery: false,
  university_names: [],
  mail_notifications: {
    reminder: true,
    thanks: true
  },
  schedules: [],
  resume_questions: []
};

/**
 * NOTE: 今後バックエンド側で変更する予定
 */
const maybeRemovePreferredPublishAt = (
  jobEvent: ApiRequest.DraftJobEvent["draft_event"]
) => {
  return jobEvent.scheduled_publish
    ? jobEvent
    : {
        ...jobEvent,
        preferred_publish_at: null
      };
};

const maybeRemoveLimitedPlace = (
  jobEvent: ApiRequest.DraftJobEvent["draft_event"]
) => {
  return jobEvent.is_lottery
    ? jobEvent
    : {
        ...jobEvent,
        schedules: jobEvent.schedules.map(schedule => {
          return {
            ...schedule,
            limited_place: ""
          };
        })
      };
};

/**
 * 編集用スケジュールデータをAPIパラメータ用に整形
 */
const formatSchedule = (
  schedule: PartiallyPartial<EditingJobEventSchedule, "mail_template">
): PartiallyPartial<JobEventSchedule, "mail_template"> => {
  switch (schedule.schedule_type) {
    case "online":
      return {
        ...schedule,
        prefecture: "",
        place: "",
        limited_place: ""
      };
    case "offline":
      return {
        ...schedule,
        prefecture: schedule.prefecture ? schedule.prefecture : "",
        live_url: "",
        live_password: ""
      };
    default:
      return schedule;
  }
};

const attentionDescriptionItem = {
  id: undefined,
  label: formConfig["attention_item"].label,
  content: formConfig["attention_item"].content,
  position: -1
};

/**
 * editingJobEvent を event作成用の構造に変換
 */
export const fixEventForCreate = (
  editingJobEvent: ApiRequest.EditingDraftJobEvent
): ApiRequest.DraftJobEvent["draft_event"] => {
  const isExternalReceptionEvent =
    editingJobEvent.reception_type === "external";

  let requestEvent = unsetEventContentId(editingJobEvent);
  requestEvent = maybeRemovePreferredPublishAt(requestEvent);
  requestEvent = maybeRemoveLimitedPlace(requestEvent);

  return {
    ...requestEvent,
    event_description_items: isExternalReceptionEvent
      ? requestEvent.event_description_items
      : [attentionDescriptionItem, ...requestEvent.event_description_items]
  };
};

/**
 * 新規作成の（draftEventの id が存在しない）場合に、event_description_items / schedules / resume_questions の id を消す
 */
const unsetEventContentId = (
  editingJobEvent: ApiRequest.DraftJobEvent["draft_event"]
): ApiRequest.DraftJobEvent["draft_event"] => {
  return {
    ...editingJobEvent,
    event_description_items: editingJobEvent.event_description_items.map(
      item => ({
        ...item,
        id: undefined
      })
    ),
    schedules: editingJobEvent.schedules.map(item => {
      return {
        ...item,
        id: undefined
      };
    }),
    resume_questions: editingJobEvent.resume_questions.map(item => ({
      ...item,
      id: undefined
    }))
  };
};

/**
 * editingJobEvent を event更新用の構造に変換
 */
export const fixEventForUpdate = (
  editingJobEvent: ApiRequest.EditingDraftJobEvent,
  originalEvent: ApiRequest.DraftJobEvent["draft_event"]
): ApiRequest.DraftJobEvent["draft_event"] => {
  const isExternalReceptionEvent =
    editingJobEvent.reception_type === "external";

  const getRequestDescriptionItems = () => {
    const draftDescriptionItems = editingJobEvent.event_description_items.map(
      item => {
        const existingItem = originalEvent.event_description_items.some(
          originalItem => originalItem.id === item.id
        );

        const isAttentionItem =
          item.label === formConfig["attention_item"].label;

        return {
          ...item,
          id: existingItem ? item.id : undefined,
          position: isAttentionItem ? -1 : item.position
        };
      }
    );

    const deletedItems = originalEvent.event_description_items
      .filter(originalItem => {
        // 項目が消されている場合
        const descriptionItemNotFound = !editingJobEvent.event_description_items.find(
          item => originalItem.id === item.id
        );

        // 外部申し込みの場合「注意事項」は削除
        const isAttentionItem =
          isExternalReceptionEvent &&
          originalItem.label === formConfig["attention_item"].label;

        return descriptionItemNotFound || isAttentionItem;
      })
      .map(item => ({
        ...item,
        _destroy: true as const
      }));

    const hasAttentionItem = draftDescriptionItems.some(
      item => item.label === formConfig["attention_item"].label
    );

    return isExternalReceptionEvent || hasAttentionItem
      ? [...draftDescriptionItems, ...deletedItems]
      : [attentionDescriptionItem, ...draftDescriptionItems, ...deletedItems];
  };

  const getRequestSchedule = () => {
    const draftSchedules = editingJobEvent.schedules.map(item => {
      const existingItem = originalEvent.schedules.some(
        originalItem => originalItem.id === item.id
      );

      const { ["mail_template"]: removedKey, ...fixedScheduleItem } = item;

      return existingItem
        ? formatSchedule(fixedScheduleItem)
        : {
            ...formatSchedule(fixedScheduleItem),
            id: undefined
          };
    });

    const deletedItems = originalEvent.schedules
      .filter(
        originalItem =>
          !editingJobEvent.schedules.some(item => originalItem.id === item.id)
      )
      .map(item => ({
        ...item,
        _destroy: true as const
      }));
    return [...draftSchedules, ...deletedItems];
  };

  const getRequestResume = () => {
    const draftResume = editingJobEvent.resume_questions.map(item => {
      const existingItem = originalEvent.resume_questions.some(
        originalItem => originalItem.id === item.id
      );

      return existingItem
        ? item
        : {
            ...item,
            id: undefined
          };
    });

    const deletedItems = originalEvent.resume_questions
      .filter(
        originalItem =>
          !editingJobEvent.resume_questions.some(
            item => originalItem.id === item.id
          )
      )
      .map(item => ({
        ...item,
        _destroy: true as const
      }));
    return [...draftResume, ...deletedItems];
  };

  let requestEvent: ApiRequest.DraftJobEvent["draft_event"] = {
    ...editingJobEvent,
    event_description_items: getRequestDescriptionItems(),
    schedules: getRequestSchedule(),
    resume_questions: getRequestResume()
  };

  requestEvent = maybeRemovePreferredPublishAt(requestEvent);
  requestEvent = maybeRemoveLimitedPlace(requestEvent);

  /**
   * draftEventの id が存在しない場合、event_description_items / schedules / resume_questions の id を消す必要がある。
   */
  if (!editingJobEvent.id) {
    return unsetEventContentId(requestEvent);
  }

  return requestEvent;
};

export const goToPreviewPage = (eventId: number) => {
  const previewUrl = `${process.env["VUE_APP_API_ORIGIN"]}/api/jobs/v1/events/${eventId}/preview`;
  window.open(previewUrl, "_blank", "noopener");
};

export const getEventDescriptionScrollKey = (id?: number) =>
  `event_description_items-${id}`;
export const getScheduleScrollKey = (id?: number | string) => `schedules-${id}`;
export const getResumeScrollKey = (id?: number) => `resume_questions-${id}`;

const forbiddenUrls = [
  "goodfind.jp", // 競合他社（Goodfind）
  "job.mynavi.jp", // 競合他社（マイナビ）
  "job.rikunabi.com", // 競合他社（リクナビAOL）
  "communicator.gakujo.ne.jp", // LINE QRコード（あさがくナビ）
  "user.digmee-connect.jp", // LINE QRコード（HR PRIME）
  "futurefinder.net" // 競合他社（FutureFinder）
];

const webMeetingUrls = [
  "zoom.us", // Web会議
  "meet.google.com", // Web会議
  "teams.live.com/meet", // Web会議
  "join.skype.com", // Web会議
  "teams.microsoft.com", // Web会議
  "us02web.zoom.us" // Web会議
];

const excludedUrls = [
  "https://mypage\\.[^/\\s]+", // 競合他社（i-web）
  "job.axol.jp" // 競合他社（マイナビAOL）
];

export const hasForbiddenUrl = (
  value?: string,
  hasExclude?: "hasExclude"
): boolean => {
  if (!value) return false;
  const urls =
    hasExclude === "hasExclude"
      ? [...forbiddenUrls, ...webMeetingUrls]
      : [...forbiddenUrls, ...webMeetingUrls, ...excludedUrls];

  const updatedForbiddenUrlPattern = new RegExp(
    urls.map(pattern => `${pattern}`).join("|"),
    "i"
  );

  return updatedForbiddenUrlPattern.test(value);
};

export const forbiddenUrlErrorMessage = (subject: string): string => {
  return `${subject}に掲載できないURLが含まれています`;
};

/**
 * hr, career, recruit, saiyo を含むURLはワーニング
 */
const warnUrlPattern = /https*:\/\/[-_~\/\.<>%:\?#/@!\$&'()\*\+,;=a-zA-Z0-9]*(hr|career|recruit|saiyo)[-_~\/\.<>%:\?#/@!\$&'()\*\+,;=a-zA-Z0-9]*/;

export const hasWarnUrl = (value?: string): boolean => {
  if (!value) return false;
  return warnUrlPattern.test(value);
};

export const warnUrlMessage =
  "採用サイトの可能性があるURLが含まれています。採用サイトのURLが記載されていた場合、予告なく公開を停止させて頂く場合があります。問題ない場合そのまま掲載依頼してください。";

/**
 * Web会議URLが記入されるようにワーニングを表示する
 */
export const isNotWebMeetingUrl = (value?: string): boolean => {
  if (!value) return false;
  const urlPattern = new RegExp(
    webMeetingUrls.map(pattern => `${pattern}`).join("|"),
    "i"
  );
  return !urlPattern.test(value);
};

export const warnWebMeetingMessage =
  "Web会議のURLを記載しているかご確認ください。";

export const findDuplicateIds = (items: JobEventDescription[]): number[] => {
  const duplicateIds: number[] = [];

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (
        items[i].content === items[j].content &&
        items[i].label === items[j].label
      ) {
        if (!duplicateIds.includes(items[i].id)) {
          duplicateIds.push(items[i].id);
        }

        duplicateIds.push(items[j].id);
        break;
      }
    }
  }

  return duplicateIds;
};

export const unsupportedSymbolErrorMessage = (subject: string): string => {
  return `${subject}に入力できない特殊記号が含まれています`;
};

// export const dirtyWordErrorMessage = (subject: string): string => {
//   return `${subject}に公序良俗に反する言葉は使用できません`;
// };

export const warnCompetitorTextMessage =
  "他社採用サイトへ誘導する内容を記載された場合、掲載停止となる場合があります。問題なければそのまま掲載依頼してください。";

type EventCategorySelectList = {
  label: string;
  value: JobEventCategory;
}[];

export const eventCategorySelectList: EventCategorySelectList = [
  { label: "説明会", value: "seminar" },
  { label: "インターン", value: "internship" },
  { label: "本選考", value: "entry" }
];

export const editableJobEventData = ["会場", "定員", "概要", "お問い合わせ先"];

/**
 * イベント種別を、表示用の日本語に変換して返す
 * @param category イベント種別
 */
export const getEventCategoryText = (category: JobEventCategory): string => {
  const target = eventCategorySelectList.find(item => item.value === category);

  return target?.label || "";
};
